import { useState } from "react";
import { History, Plus, X } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import { useDraft } from "@/components/itsm-config/draft-context";
import { StatusMappingEditor } from "@/components/itsm-config/mapping-editors";
import { MetadataSelect } from "@/components/itsm-config/metadata-select";
import { useCustomFieldDefinitions } from "@/hooks/use-custom-fields";
import { useDesks } from "@/hooks/use-desk-migration";
import { useGeneralMappingSave } from "@/hooks/use-general-mapping-save";
import { useIntegrationConfig } from "@/hooks/use-itsm-config";
import { useCreateImportJob } from "@/hooks/use-itsm-import";
import {
  useMondayColumns,
  usePmScopeName,
  usePmScopes,
  type MondayColumnOption,
} from "@/hooks/use-pm-metadata";
import { findRule } from "@/lib/itsm-config/general-mapping";
import {
  customFieldMappingPatch,
  mondayBoards,
  patchBoardSync,
} from "@/lib/itsm-config/monday";
import {
  HARMONY_STATUSES,
  HARMONY_STATUS_LABELS,
  type GeneralMatch,
  type MondayBoardSync,
  type ProviderConfig,
} from "@/lib/types/itsm-config";

interface Props {
  tenantId: string;
  providerConfig: ProviderConfig;
}

interface ImportProps extends Props {
  credentialId: string;
}

/** Status mappings are resolved per board, the way both sync directions read
 *  them: a board's labels are whatever that board's owner named them. */
function boardMatch(boardId: string): GeneralMatch {
  return { integration_id: null, provider_scope: boardId, issue_type: null };
}

// Column types Harmony knows how to write a value to. Mirrors service-desk's
// _COLUMN_TYPE_MAP: an allowlist rather than a list of computed types,
// because a board can hold types nobody listed, and a custom field mapped to
// one would fail at write time rather than here.
//
// People columns are left out: writing one takes a monday user id, and
// Harmony has no way to turn an employee into one yet.
const WRITABLE_COLUMN_TYPES = new Set([
  "text",
  "long_text",
  "email",
  "link",
  "numbers",
  "date",
  "status",
  "dropdown",
]);

function columnOptions(
  columns: MondayColumnOption[] | undefined,
  types: string[],
) {
  return (columns ?? [])
    .filter((c) => types.includes(c.column_type))
    .map((c) => ({ value: c.value, label: c.label }));
}

function useBoardSyncPatch() {
  const { update } = useDraft();
  return (boardId: string, patch: Partial<MondayBoardSync>) => {
    update((current) => patchBoardSync(current, boardId, patch));
  };
}

function BoardTitle({
  tenantId,
  boardId,
}: {
  tenantId: string;
  boardId: string;
}) {
  const name = usePmScopeName("monday", tenantId, boardId);
  return <p className="text-sm font-medium">{name || boardId}</p>;
}

function NoBoards() {
  return (
    <p className="text-muted-foreground py-4 text-center text-sm">
      No boards yet. Add an export rule under &quot;Where tickets go&quot;
      first.
    </p>
  );
}

function ColumnPicker({
  label,
  hint,
  options,
  isLoading,
  value,
  onChange,
}: {
  label: string;
  hint: string;
  options: { value: string; label: string }[];
  isLoading: boolean;
  value: string | null | undefined;
  onChange: (next: string | null) => void;
}) {
  return (
    <div className="space-y-1">
      <Label className="text-xs">{label}</Label>
      <div className="flex items-center gap-1">
        <MetadataSelect
          options={options}
          isLoading={isLoading}
          value={value ?? ""}
          onChange={(v) => {
            onChange(v || null);
          }}
          placeholder="Not synced"
          allowCustom={false}
          className="w-[200px]"
        />
        {value && (
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={() => {
              onChange(null);
            }}
          >
            <X className="h-4 w-4" />
            <span className="sr-only">Clear</span>
          </Button>
        )}
      </div>
      <p className="text-muted-foreground text-xs">{hint}</p>
    </div>
  );
}

function BoardColumns({
  tenantId,
  boardId,
  sync,
}: {
  tenantId: string;
  boardId: string;
  sync: MondayBoardSync | undefined;
}) {
  const columns = useMondayColumns(tenantId, boardId);
  const patch = useBoardSyncPatch();
  const list = columns.data?.options;

  return (
    <div className="space-y-3 rounded-md border p-3">
      <BoardTitle tenantId={tenantId} boardId={boardId} />
      <div className="flex flex-wrap gap-4">
        <ColumnPicker
          label="Status column"
          hint="Mirrors the ticket status, both ways."
          options={columnOptions(list, ["status"])}
          isLoading={columns.isLoading}
          value={sync?.status_column_id}
          onChange={(v) => {
            patch(boardId, { status_column_id: v });
          }}
        />
        <ColumnPicker
          label="Assignee column"
          hint="Read back as the ticket's assignee."
          options={columnOptions(list, ["people", "person"])}
          isLoading={columns.isLoading}
          value={sync?.assignee_column_id}
          onChange={(v) => {
            patch(boardId, { assignee_column_id: v });
          }}
        />
        <ColumnPicker
          label="Files column"
          hint="Files added here become ticket attachments."
          options={columnOptions(list, ["file"])}
          isLoading={columns.isLoading}
          value={sync?.files_column_id}
          onChange={(v) => {
            patch(boardId, { files_column_id: v });
          }}
        />
      </div>
    </div>
  );
}

/** monday: the two-way sync switch and the columns each board syncs through. */
export function MondayTwoWaySyncSection({ tenantId, providerConfig }: Props) {
  const { update } = useDraft();
  const enabled = providerConfig.two_way_sync ?? false;
  const boards = mondayBoards(providerConfig);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Two-way sync</CardTitle>
        <CardDescription>
          Keep each item in step with its ticket after it is created. Status and
          messages go to monday; the item&apos;s status, assignee, comments and
          files come back to the ticket. Pair more fields, either way, under
          &quot;Field mappings&quot;.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-start gap-3 rounded-md border px-4 py-3">
          <div className="flex-1 space-y-0.5">
            <p className="text-sm font-medium">Sync both ways</p>
            <p className="text-muted-foreground text-sm">
              {enabled
                ? "On for every monday item created from a ticket, including " +
                  "manual exports."
                : "Off — items are created and then left alone."}
            </p>
          </div>
          <Switch
            checked={enabled}
            onCheckedChange={(v) => {
              update({ two_way_sync: v });
            }}
          />
        </div>

        {boards.length === 0 ? (
          <NoBoards />
        ) : (
          boards.map((boardId) => (
            <BoardColumns
              key={boardId}
              tenantId={tenantId}
              boardId={boardId}
              // eslint-disable-next-line security/detect-object-injection -- boardId comes from our own config
              sync={providerConfig.board_sync?.[boardId]}
            />
          ))
        )}
      </CardContent>
    </Card>
  );
}

function BoardStatusMapping({
  tenantId,
  boardId,
  providerConfig,
}: Props & { boardId: string }) {
  const save = useGeneralMappingSave();
  const columns = useMondayColumns(tenantId, boardId);
  // eslint-disable-next-line security/detect-object-injection -- boardId comes from our own config
  const statusColumn = providerConfig.board_sync?.[boardId]?.status_column_id;
  const labels =
    columns.data?.options.find((c) => c.value === statusColumn)?.labels ?? [];
  const match = boardMatch(boardId);

  return (
    <div className="space-y-2 rounded-md border p-3">
      <BoardTitle tenantId={tenantId} boardId={boardId} />
      {!statusColumn && (
        <p className="text-muted-foreground text-xs">
          Pick this board&apos;s status column under &quot;Two-way sync&quot; to
          choose from its labels.
        </p>
      )}
      <StatusMappingEditor
        value={
          findRule(providerConfig.general_mapping, match)?.status_mapping ?? {}
        }
        onChange={(m) => {
          save(match, { status_mapping: Object.keys(m).length ? m : null });
        }}
        harmonyOptions={HARMONY_STATUSES}
        harmonyLabels={HARMONY_STATUS_LABELS}
        providerLabel="monday"
        externalOptions={labels.map((l) => ({ value: l, label: l }))}
        bidirectional
      />
    </div>
  );
}

/** monday: Harmony status ↔ status label, per board. */
export function MondayStatusMappingSection({
  tenantId,
  providerConfig,
}: Props) {
  const boards = mondayBoards(providerConfig);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Status translation</CardTitle>
        <CardDescription>
          Map each Harmony status to the board&apos;s status labels. Used both
          ways: outbound, the first label is set; inbound, any of them moves the
          ticket to that status. A label mapped from two Harmony statuses is
          ignored inbound, and an unmapped status is left alone.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {boards.length === 0 ? (
          <NoBoards />
        ) : (
          boards.map((boardId) => (
            <BoardStatusMapping
              key={boardId}
              tenantId={tenantId}
              boardId={boardId}
              providerConfig={providerConfig}
            />
          ))
        )}
      </CardContent>
    </Card>
  );
}

function BoardCustomFields({
  tenantId,
  boardId,
  deskIds,
  mapping,
  createOnly,
}: {
  tenantId: string;
  boardId: string;
  deskIds: string[];
  mapping: Record<string, string>;
  createOnly: string[];
}) {
  const columns = useMondayColumns(tenantId, boardId);
  const fields = useCustomFieldDefinitions(tenantId, deskIds);
  const patch = useBoardSyncPatch();
  // A row is added with both halves chosen, so the draft never carries a
  // field mapped to no column.
  const [newField, setNewField] = useState("");
  const [newColumn, setNewColumn] = useState("");

  const writable = (columns.data?.options ?? [])
    .filter((c) => WRITABLE_COLUMN_TYPES.has(c.column_type))
    .map((c) => ({ value: c.value, label: `${c.label} (${c.column_type})` }));
  const { data: desks } = useDesks(tenantId);
  // Mapping keys are config field ids, or legacy definition ids for mappings
  // saved before the configs; names repeat across desks, so the label says
  // which desk a field belongs to.
  const fieldLabel = (id: string) => {
    const def =
      fields.definitions.find((d) => d.id === id) ??
      fields.legacyDefinitions.find((d) => String(d.id) === id);
    if (!def) return `Deleted field #${id}`;
    const desk = def.desk_id
      ? (desks?.find((d) => d.id === def.desk_id)?.name ?? def.desk_id)
      : "all desks";
    return `${def.name} · ${desk}`;
  };
  // A field already mapped under its legacy id is the same field by desk and
  // name; offering it again would map it twice.
  const legacyMapped = new Set(
    fields.legacyDefinitions
      .filter((d) => String(d.id) in mapping)
      .map((d) => `${d.desk_id ?? ""}\u0000${d.name}`),
  );
  const unmapped = fields.definitions
    .filter(
      (d) =>
        !(d.id in mapping) &&
        !legacyMapped.has(`${d.desk_id ?? ""}\u0000${d.name}`),
    )
    .map((d) => ({
      value: d.id,
      label: `${fieldLabel(d.id)} (${d.field_type})`,
    }));

  const persist = (
    next: Record<string, string>,
    nextCreateOnly: readonly string[] = createOnly,
  ) => {
    patch(boardId, customFieldMappingPatch(next, nextCreateOnly));
  };

  return (
    <div className="space-y-2 rounded-md border p-3">
      <BoardTitle tenantId={tenantId} boardId={boardId} />
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[240px]">Harmony custom field</TableHead>
            <TableHead>monday column</TableHead>
            <TableHead className="w-28">Create only</TableHead>
            <TableHead className="w-10" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {Object.keys(mapping).length === 0 ? (
            <TableRow>
              <TableCell
                colSpan={4}
                className="text-muted-foreground text-center text-sm"
              >
                No mappings
              </TableCell>
            </TableRow>
          ) : (
            Object.entries(mapping).map(([field, column]) => (
              <TableRow key={field}>
                <TableCell className="text-sm">
                  {fields.isLoading ? field : fieldLabel(field)}
                </TableCell>
                <TableCell>
                  <MetadataSelect
                    options={writable}
                    isLoading={columns.isLoading}
                    value={column}
                    onChange={(v) => {
                      persist({ ...mapping, [field]: v });
                    }}
                    placeholder="Select a column..."
                    allowCustom={false}
                  />
                </TableCell>
                <TableCell>
                  <Switch
                    checked={createOnly.includes(field)}
                    aria-label={`Create only: ${fieldLabel(field)}`}
                    onCheckedChange={(on) => {
                      persist(
                        mapping,
                        on
                          ? [...createOnly, field]
                          : createOnly.filter((k) => k !== field),
                      );
                    }}
                  />
                </TableCell>
                <TableCell>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    aria-label={`Remove mapping for ${fieldLabel(field)}`}
                    onClick={() => {
                      const { [field]: _omit, ...rest } = mapping;
                      persist(rest);
                    }}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
      <div className="flex items-center gap-2">
        <div className="w-[240px]">
          <MetadataSelect
            options={unmapped}
            isLoading={fields.isLoading}
            value={newField}
            onChange={setNewField}
            placeholder="Add a custom field..."
            allowCustom={false}
          />
        </div>
        <div className="w-[240px]">
          <MetadataSelect
            options={writable}
            isLoading={columns.isLoading}
            value={newColumn}
            onChange={setNewColumn}
            placeholder="...to a column"
            allowCustom={false}
          />
        </div>
        <Button
          variant="outline"
          size="sm"
          disabled={!newField || !newColumn}
          onClick={() => {
            persist({ ...mapping, [newField]: newColumn });
            setNewField("");
            setNewColumn("");
          }}
        >
          <Plus className="mr-1 h-4 w-4" />
          Add
        </Button>
      </div>
    </div>
  );
}

/** monday: which column each Harmony custom field is written to, per board. */
export function MondayCustomFieldsSection({ tenantId, providerConfig }: Props) {
  const boards = mondayBoards(providerConfig);
  const deskIds = [
    ...new Set(
      (providerConfig.export_mapping ?? [])
        .map((r) => r.match.desk_id)
        .filter((d): d is string => Boolean(d)),
    ),
  ];

  return (
    <Card>
      <CardHeader>
        <CardTitle>Custom fields</CardTitle>
        <CardDescription>
          Choose the board column each Harmony custom field is written to.
          Written only with two-way sync on: the column is filled when the item
          is created and rewritten when the field changes in Harmony. Turn on
          &quot;Create only&quot; to fill it at creation and leave it to the
          board after that. Custom fields are never read back from monday.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {boards.length === 0 ? (
          <NoBoards />
        ) : (
          boards.map((boardId) => (
            <BoardCustomFields
              key={boardId}
              tenantId={tenantId}
              boardId={boardId}
              deskIds={deskIds}
              mapping={
                // eslint-disable-next-line security/detect-object-injection -- boardId comes from our own config
                providerConfig.board_sync?.[boardId]?.custom_field_columns ?? {}
              }
              createOnly={
                // eslint-disable-next-line security/detect-object-injection -- boardId comes from our own config
                providerConfig.board_sync?.[boardId]
                  ?.create_only_custom_fields ?? []
              }
            />
          ))
        )}
      </CardContent>
    </Card>
  );
}

function BoardImport({
  tenantId,
  credentialId,
  boardId,
  sync,
}: {
  tenantId: string;
  credentialId: string;
  boardId: string;
  sync: MondayBoardSync | undefined;
}) {
  const patch = useBoardSyncPatch();
  const { data: desks, isLoading: desksLoading } = useDesks(tenantId);
  const backfill = useCreateImportJob(tenantId);
  const { data: saved } = useIntegrationConfig(tenantId, credentialId);
  const enabled = sync?.import_enabled ?? false;
  const deskId = sync?.import_desk_id ?? "";
  const live = enabled && Boolean(deskId);
  // The backfill reads the saved config - this board's status mapping and
  // columns - so it starts only once what is shown here has been saved.
  // eslint-disable-next-line security/detect-object-injection -- boardId comes from our own config
  const savedSync = saved?.config.provider_config?.board_sync?.[boardId];
  const savedLive =
    Boolean(savedSync?.import_enabled) && savedSync?.import_desk_id === deskId;

  const startBackfill = () => {
    backfill.mutate(
      {
        source: "provider",
        provider: "monday",
        credential_id: credentialId,
        source_project: boardId,
        target_desk_id: deskId,
      },
      {
        onSuccess: () => {
          toast.success("Backfill started. Follow it on the ITSM Import page.");
        },
        onError: (error) => {
          toast.error(`Backfill not started: ${error.message}`);
        },
      },
    );
  };

  return (
    <div className="space-y-3 rounded-md border p-3">
      <div className="flex items-start gap-3">
        <div className="flex-1">
          <BoardTitle tenantId={tenantId} boardId={boardId} />
          <p className="text-muted-foreground text-xs">
            {live
              ? "New rows become tickets in the desk below."
              : enabled
                ? "Choose a desk - nothing is imported without one."
                : "Off - rows on this board are not imported."}
          </p>
        </div>
        <Switch
          checked={enabled}
          aria-label="Import this board"
          onCheckedChange={(v) => {
            patch(boardId, { import_enabled: v });
          }}
        />
      </div>
      <div className="flex flex-wrap items-end gap-4">
        <div className="space-y-1">
          <Label className="text-xs">Desk</Label>
          <MetadataSelect
            options={(desks ?? []).map((d) => ({ value: d.id, label: d.name }))}
            isLoading={desksLoading}
            value={deskId}
            onChange={(v) => {
              patch(boardId, { import_desk_id: v || null });
            }}
            placeholder="Select a desk"
            allowCustom={false}
            className="w-[240px]"
          />
        </div>
        <Button
          variant="outline"
          size="sm"
          disabled={!live || !savedLive || backfill.isPending}
          onClick={startBackfill}
        >
          <History className="mr-1 h-4 w-4" />
          Import existing rows
        </Button>
      </div>
      <p className="text-muted-foreground text-xs">
        Existing rows are imported once, by a backfill job. Save the config
        first: the job reads this board&apos;s saved status mapping.
      </p>
    </div>
  );
}

/** monday: which boards are a source of tickets, and the desk each feeds. */
export function MondayImportSection({
  tenantId,
  credentialId,
  providerConfig,
}: ImportProps) {
  const patch = useBoardSyncPatch();
  const scopes = usePmScopes("monday", tenantId);
  const [newBoard, setNewBoard] = useState("");
  const boards = mondayBoards(providerConfig);
  const addable = (scopes.data?.options ?? []).filter(
    (o) => !boards.includes(o.value),
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle>Import from boards</CardTitle>
        <CardDescription>
          Turn a board into a source of tickets. Its rows become tickets in the
          chosen desk, and the row owns them: name, columns, status, assignee,
          updates and files all flow onto the ticket, and the ticket&apos;s
          status is written back to the board&apos;s status column (pick it
          under &quot;Two-way sync&quot; and map it under &quot;Status
          translation&quot;). Independent of two-way sync - exporting to a board
          never imports its rows.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {boards.map((boardId) => (
          <BoardImport
            key={boardId}
            tenantId={tenantId}
            credentialId={credentialId}
            boardId={boardId}
            // eslint-disable-next-line security/detect-object-injection -- boardId comes from our own config
            sync={providerConfig.board_sync?.[boardId]}
          />
        ))}
        <div className="flex items-center gap-2">
          <div className="w-[320px]">
            <MetadataSelect
              options={addable}
              isLoading={scopes.isLoading}
              value={newBoard}
              onChange={setNewBoard}
              placeholder="Add a board to import from..."
              allowCustom={false}
            />
          </div>
          <Button
            variant="outline"
            size="sm"
            disabled={!newBoard}
            onClick={() => {
              patch(newBoard, { import_enabled: true });
              setNewBoard("");
            }}
          >
            <Plus className="mr-1 h-4 w-4" />
            Add
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
